import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, HandHeart, Scale, Timer } from "lucide-react";
import { api } from "../lib/api";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine } from "../components/Reveal";

const IMG = "https://images.unsplash.com/photo-1647381518264-97ff1835026f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwZXJzb25fa2l0Y2hlbl9zdGFmZnw&q=85";

const VALUES = [
  { icon: Timer, title: "Reliability", text: "We show up when we say we will, on a schedule you can plan around." },
  { icon: ClipboardCheck, title: "Attention to detail", text: "Checklist-driven visits mean the same thorough result every time." },
  { icon: HandHeart, title: "Customer care", text: "You talk to real people who know your space and remember your preferences." },
  { icon: Scale, title: "Fair pricing", text: "Written, itemized estimates - everything included, nothing hidden." },
];

export default function About() {
  const [aboutText, setAboutText] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setAboutText(r.data.about_text || "")).catch(() => {});
  }, []);

  return (
    <div>
      <Seo
        title="About Glow Green | Cleaning Company in St. Louis & Chesterfield, MO"
        description="About Glow Green: a locally focused cleaning company serving St. Louis and Chesterfield, MO with reliability, attention to detail, customer care, and fair pricing."
      />

      <section className="bg-forest grain relative pt-32 lg:pt-44 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <p className="uppercase text-xs font-semibold tracking-[0.25em] text-leaf font-mono">About Glow Green</p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-3xl">
            <MaskedLine delay={0.2}>Clean done right,</MaskedLine>
            <MaskedLine delay={0.35} className="text-leaf">priced done fairly.</MaskedLine>
          </h1>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="overflow-hidden rounded-2xl clipped-frame aspect-[4/5]">
              <img src={IMG} alt="Glow Green professional cleaner in a warm modern home" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-forest">Our story</h2>
            <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
              {(aboutText || "Loading...").split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-forest grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative z-10">
          <Reveal>
            <p className="uppercase text-xs font-semibold tracking-[0.2em] text-leaf">What we stand for</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Four promises, kept daily.</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="rounded-2xl bg-forest-soft/60 border border-white/10 p-6 h-full hover:border-leaf/40 transition-colors">
                  <v.icon className="w-6 h-6 text-leaf" />
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <Link to="/contact" data-testid="about-quote-cta" className="inline-flex items-center gap-2 rounded-full bg-leaf px-8 py-4 font-display font-bold text-sm text-forest-deep transition-all hover:bg-mint hover:scale-[1.03]">
                Work With Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
